<template>
  <div class="content">
    <!-- PDF 열기 / zip 생성 대기 표시 — 서버 병합·전송이 수 초 이상 걸릴 수 있어 진행 중임을 보여준다 -->
    <div v-if="busyMessage" class="busy-overlay">
      <div class="busy-box">
        <div class="busy-spinner" />
        <p>{{ busyMessage }}</p>
      </div>
    </div>
    <div class="unity-box">
      <div v-show="isLoading" class="loading-bar">
        <p>Loading... {{ loadingProgress }}%</p>
      </div>

      <VueUnity
        :unity="unityContext"
        width="100%"
        height="100%"
        ref="unityContainer"
      />

      <!-- 레이어 팝업 -->
      <div class="layer-popup" v-if="isPopupVisible" @click.self="closePopup">
        <div class="popup-content">
          <!-- 닫기 버튼 -->
          <button @click="closePopup" class="close-button">✖</button>
          <!-- 팝업 콘텐츠를 여기에 추가하세요 -->
          <h3>{{ popupTitle }}</h3>
          <div>
            <PDFViewer
              ref="pdfViewer"
              :source="pdfSource"
              :controls="pdfControls"
              @download="handleDownload"
              style="
                margin-top: 0px;
                margin-left: 0vh;
                height: 80vh;
                width: 63vw;
              "
            />
          </div>
        </div>
      </div>
    </div>
    <aside class="web-box cab-panel">
      <!-- 상단: 현장명 + 뒤로 -->
      <header class="cab-header">
        <div class="cab-brand">
          <img src="/img/common/logo.svg" alt="" class="cab-logo" />
          <div class="cab-site" :title="site.name">{{ site.name }}</div>
        </div>
        <button
          type="button"
          class="cab-back"
          :disabled="activeTab === 'tab0'"
          @click="goBack"
        >
          <span class="cab-back-arrow">‹</span> 뒤로
        </button>
      </header>

      <!-- 경로(방 › 월 › 구분) -->
      <nav class="cab-crumbs" v-if="activeTab !== 'tab0'">
        <span class="cab-crumb" :class="{ current: crumbs.length === 1 }">
          {{ crumbs[0] }}
        </span>
        <template v-for="(crumb, i) in crumbs.slice(1)">
          <span :key="'sep' + i" class="cab-crumb-sep">›</span>
          <span
            :key="'crumb' + i"
            class="cab-crumb"
            :class="{ current: i === crumbs.length - 2 }"
          >
            {{ crumb }}
          </span>
        </template>
      </nav>

      <section class="cab-body">
        <!-- 섹션 제목 + 액션 -->
        <div class="cab-section-head">
          <h2 class="cab-section-title">{{ sectionTitle }}</h2>
          <div class="cab-section-meta">
            <span v-if="activeTab === 'tab3' && testbookList" class="cab-count">
              {{ testbookList.length }}건
            </span>
            <button
              v-if="activeTab === 'tab3'"
              type="button"
              class="cab-action"
              :disabled="!testbookList || !testbookList.length"
              @click="pdfZip"
            >
              PDF 일괄 다운로드
            </button>
          </div>
        </div>

        <!-- tab0: 방 선택 -->
        <div v-if="activeTab === 'tab0'" class="cab-scroll">
          <button type="button" class="cab-room" @click="sceneMove(1)">
            <span class="cab-room-icon cab-ic-cabinet" />
            <span class="cab-room-text">
              <strong>월별성적서방</strong>
              <small>월 → 구분 순으로 성적서를 찾습니다</small>
            </span>
            <span class="cab-room-go">›</span>
          </button>
          <button type="button" class="cab-room" @click="sceneMove(2)">
            <span class="cab-room-icon cab-ic-file" />
            <span class="cab-room-text">
              <strong>공종별성적서방</strong>
              <small>구분 → 월 순으로 성적서를 찾습니다</small>
            </span>
            <span class="cab-room-go">›</span>
          </button>
        </div>

        <!-- tab1: 월 타일 -->
        <div v-if="activeTab === 'tab1'" class="cab-scroll">
          <p v-if="!cabinetList || !cabinetList.length" class="cab-empty">
            표시할 월이 없습니다.
          </p>
          <div v-else class="cab-grid">
            <button
              v-for="cabinet in recentFirstCabinets"
              :key="cabinet.id"
              type="button"
              class="cab-tile"
              :class="{ active: cabinet.name === activeDate }"
              @click="onClickCabinet(cabinet)"
            >
              <span class="cab-tile-icon cab-ic-cabinet" />
              <span class="cab-tile-label">{{ monthLabel(cabinet.name) }}</span>
            </button>
          </div>
        </div>

        <!-- tab2: 구분(공종) 타일 -->
        <div v-if="activeTab === 'tab2'" class="cab-scroll">
          <p v-if="!categoryList || !categoryList.length" class="cab-empty">
            해당 조건에 승인된 성적서가 없습니다.
          </p>
          <div v-else class="cab-grid">
            <button
              v-for="(category, index) in categoryList"
              :key="index"
              type="button"
              class="cab-tile"
              :class="{ active: category.code === activeCategory }"
              @click="onClickCategory(category)"
            >
              <span class="cab-tile-icon cab-ic-file" />
              <span class="cab-tile-label">{{ category.name }}</span>
            </button>
          </div>
        </div>

        <!-- tab3: 성적서 목록 -->
        <div v-if="activeTab === 'tab3'" class="cab-scroll">
          <p v-if="!testbookList || !testbookList.length" class="cab-empty">
            승인된 성적서가 없습니다.
          </p>
          <ul v-else class="cab-list">
            <li
              v-for="(testbook, index) in testbookList"
              :key="testbook.id || index"
              class="cab-row"
              :title="testbook.testNo + ' ' + (testbook.testItemName || '')"
              @click="detailPdf(testbook)"
            >
              <span class="cab-row-icon cab-ic-pdf" />
              <span class="cab-row-main">
                <span class="cab-row-no">{{ testbook.testNo }}</span>
                <span class="cab-row-sub">{{ rowSub(testbook) }}</span>
              </span>
              <span v-if="testbook.testCount > 1" class="cab-badge">
                {{ testbook.testCount }}건
              </span>
              <span class="cab-row-go">›</span>
            </li>
          </ul>
        </div>
      </section>
    </aside>
  </div>
</template>

<script>
import { JWT_TOKEN } from "@/const";
import UnityWebgl from "unity-webgl";
import VueUnity from "unity-webgl/vue";
import CabinetApi from "@/api/CabinetApi";
import SiteApi from "@/api/SiteApi";
import { mapActions } from "vuex";
import PDFViewer from "pdf-viewer-vue/dist/vue2-pdf-viewer";
import { reportClient } from "@/network/httpclient/HttpClientFactory";
const server = "";
const Unity = new UnityWebgl({
  loaderUrl: server + "/unity/metaCabinet.loader.js",
  dataUrl: server + "/unity/metaCabinet.data",
  frameworkUrl: server + "/unity/metaCabinet.framework.js",
  codeUrl: server + "/unity/metaCabinet.wasm",
});

window.ReceiveObjectName = function ReceiveObjectName(data) {
  window.vueInstance.ReceiveObjectName(data);
};

export default {
  components: {
    VueUnity,
    PDFViewer,
  },
  data() {
    return {
      unityContext: Unity,
      cabinet: null,
      monthCabinet: null,
      categoryCabinet: null,
      cabinetList: null,
      site: { name: "" },
      roomName: "월별성적서방",
      categoryList: null,
      activeTab: "tab1", // 기본적으로 탭1을 활성화
      activeDate: null,
      activeCategory: null,
      activeCategoryName: null, // 경로 표시용(코드가 아닌 이름)
      activeScene: 1,
      testbookList: null,
      isPopupVisible: false,
      pdfSource: "",
      pdfControls: [
        "rotate",
        "zoom",
        "catalog",
        "switchPage",
        "download",
        "print",
      ],
      popupTitle: "",
      isLoading: true, // 로딩 상태를 나타내는 변수
      loadingProgress: 0, // 로딩 진행 상황
      busyMessage: "", // PDF 열기·zip 생성 중 안내 문구(빈 문자열이면 숨김)
    };
  },
  computed: {
    /** 경로 표시: 방 → (월 → 구분 | 구분 → 월). 마지막 항목이 현재 단계 */
    crumbs() {
      const list = [this.roomName];
      const month = this.activeDate ? this.monthLabel(this.activeDate) : null;
      const cat = this.activeCategoryName;
      if (this.activeScene === 1) {
        if (this.activeTab !== "tab1" && month) list.push(month);
        if (this.activeTab === "tab3" && cat) list.push(cat);
      } else {
        if (this.activeTab !== "tab2" && cat) list.push(cat);
        if (this.activeTab === "tab3" && month) list.push(month);
      }
      return list;
    },
    /**
     * 월 타일은 최신 월이 먼저. API 는 오래된 순(id 순)이고 그 id 가 Unity 3D 캐비넷과
     * 매핑되므로 원본 배열은 두고 표시용으로만 복사·정렬한다("yy.mm" 문자열 비교로 충분).
     */
    recentFirstCabinets() {
      if (!this.cabinetList) return [];
      return [...this.cabinetList].sort((a, b) =>
        String(b.name).localeCompare(String(a.name))
      );
    },
    sectionTitle() {
      switch (this.activeTab) {
        case "tab0":
          return "성적서방 선택";
        case "tab1":
          return "월 선택";
        case "tab2":
          return "구분 선택";
        case "tab3":
          return "성적서 목록";
        default:
          return "";
      }
    },
  },
  watch: {},
  beforeCreate() {},
  created() {},
  mounted() {
    let token = localStorage.getItem(JWT_TOKEN);

    // 토큰 console.log는 민감정보 유출이라 제거
    if (!token) {
      const queryString = window.location.search;
      const urlParams = new URLSearchParams(queryString);

      token = urlParams.get("token");
      if (token) {
        localStorage.setItem(JWT_TOKEN, token);
        // 토큰이 브라우저 히스토리·서버 로그에 남지 않도록 URL에서 즉시 제거
        urlParams.delete("token");
        const query = urlParams.toString();
        window.history.replaceState(
          window.history.state,
          "",
          window.location.pathname + (query ? `?${query}` : "")
        );
      }

      if (!token) this.$router.push({ name: "Login" });
    }

    SiteApi.get().then((res) => {
      this.site = res.content;
    });
    this.cabinetInit();
    window.vueInstance = this;

    Unity.on("device", () => alert("click device ..."));

    // 로딩 진행 상황 업데이트
    Unity.on("progress", (percent) => {
      console.log("Unity progress: ", percent);
      this.loadingProgress = Math.round(percent * 100); // 퍼센트 업데이트
      if (percent === 1) {
        this.isLoading = false; // 로딩 완료되면 로딩바 숨기기
      }
    })
      .on("loaded", () => console.log("Unity loaded: success"))
      .on("created", () => {
        console.log("Unity created: success");
        this.unitySuccess();
      });
  },
  beforeDestroy() {},
  destroy() {},
  methods: {
    ...mapActions([]),
    async unitySuccess() {
      if (!this.cabinet) await this.cabinetInit();
      this.unityContext.send(
        "makeCabinetSet",
        "sendAllData",
        JSON.stringify(this.cabinet)
      );
    },
    async cabinetInit() {
      const result = await CabinetApi.cabinetInit();
      this.monthCabinet = result[0].cabinet;
      this.cabinetList = this.monthCabinet;
      this.categoryList = result[1].cabinet;
      this.cabinet = { room: result };
    },
    ReceiveObjectName(data) {
      const cabinetId = data.split("@")[1];

      const findDate = this.monthCabinet.find(
        (data) => data.id === Number(cabinetId)
      );
      this.activeDate = findDate.name;
      this.onClickCabinet(findDate);
    },
    async onClickCabinet(cabinet) {
      this.activeDate = cabinet.name;
      if (this.activeScene === 1) {
        this.unityContext.send(
          "clickEvent",
          "SendSearchData",
          String(cabinet.id)
        );

        const result = await CabinetApi.monthCategory(cabinet.name);
        this.categoryList = result;
        this.activeTab = "tab2";
      } else {
        const result = await CabinetApi.testbookList(
          this.activeDate,
          this.activeCategory
        );
        this.testbookList = result;
        this.activeTab = "tab3";
      }
    },
    async onClickCategory(category) {
      if (category && category.code) {
        this.activeCategory = category.code;
        this.activeCategoryName = category.name;
      }
      if (this.activeScene === 1) {
        const result = await CabinetApi.testbookList(
          this.activeDate,
          this.activeCategory
        );
        this.testbookList = result;
        this.activeTab = "tab3";
      } else {
        const result = await CabinetApi.cabinetCategory(this.activeCategory);
        this.categoryCabinet = result;
        this.activeTab = "tab1";
        this.cabinetList = this.categoryCabinet;
      }
    },
    sceneMove(scene) {
      this.activeScene = scene;
      this.unityContext.send("makeCabinetSet", "sendSceneMove", String(scene));

      if (this.activeScene === 1) {
        this.cabinetList = this.monthCabinet;
        this.activeTab = "tab1";
        this.roomName = "월별성적서방";
      }
      if (this.activeScene === 2) {
        this.activeTab = "tab2";
        this.roomName = "공종별성적서방";
      }
    },
    goBack() {
      this.unityContext.send("clickEvent", "sendColorReset");
      if (this.activeScene === 1 && this.activeTab === "tab1") {
        this.activeTab = "tab0";
        return;
      }
      if (this.activeScene === 1 && this.activeTab === "tab2") {
        this.activeTab = "tab1";
        return;
      }
      if (this.activeScene === 1 && this.activeTab === "tab3") {
        this.activeTab = "tab2";
        return;
      }

      if (this.activeScene === 2 && this.activeTab === "tab1") {
        this.activeTab = "tab2";
        return;
      }
      if (this.activeScene === 2 && this.activeTab === "tab2") {
        this.activeTab = "tab0";
        return;
      }
      if (this.activeScene === 2 && this.activeTab === "tab3") {
        this.activeTab = "tab1";
        return;
      }
    },
    openPopup() {
      this.isPopupVisible = true;
    },
    closePopup() {
      this.isPopupVisible = false;
    },
    /** 목록 보조줄: 시험항목 · 자재(있을 때만) */
    rowSub(testbook) {
      return [testbook.testItemName, testbook.material]
        .filter(Boolean)
        .join(" · ");
    },
    /** "26.08" → "2026년 08월". 형식이 다르면 원문 그대로 */
    monthLabel(name) {
      const m = /^(\d{2})\.(\d{2})$/.exec(name || "");
      return m ? `20${m[1]}년 ${m[2]}월` : name;
    },
    notifyError(msg) {
      if (this.$toast && this.$toast.error) this.$toast.error(msg);
      else alert(msg);
    },
    async detailPdf(testbook) {
      if (this.busyMessage) return; // 진행 중 중복 클릭 방지
      let id = testbook.id;
      if (testbook.groupTestId) {
        id = testbook.groupTestId;
      }

      this.busyMessage = "성적서 PDF를 불러오는 중입니다…";
      try {
        const url = "/testbook-pdf/" + id;
        const response = await reportClient.post(url, null, {
          responseType: "blob",
        });
        if (!response || !response.data || response.data.size === 0) {
          this.notifyError("생성된 PDF 파일이 없습니다.");
          return;
        }

        // 이전에 연 blob URL 은 회수(누수 방지)
        if (this.pdfSource) window.URL.revokeObjectURL(this.pdfSource);
        const blob = new Blob([response.data], { type: "application/pdf" });
        this.pdfSource = window.URL.createObjectURL(blob);

        this.popupTitle = testbook.testNo;
        this.isPopupVisible = true;
        this.$nextTick(() => this.fixPdfViewerViewport());
      } catch (e) {
        this.notifyError(
          "PDF를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요."
        );
      } finally {
        this.busyMessage = "";
      }
    },
    // pdf-viewer-vue 버그 보정: 뷰어가 iframe 콘텐츠 마운트 전에 뷰포트 폭을
    // 측정하면 0이 되어 페이지 폭이 음수(-40px)로 계산돼 점처럼 수축한다.
    // 측정 가능해질 때까지 재시도해서 뷰어 내부 상태에 직접 주입한다.
    fixPdfViewerViewport(retry = 0) {
      if (!this.isPopupVisible) return;
      const viewer = this.$refs.pdfViewer && this.$refs.pdfViewer.$refs.viewer;
      const scroller = viewer && viewer.$refs.viewerScroller;
      if (scroller && scroller.clientWidth > 0) {
        viewer.viewportWidth = scroller.clientWidth;
        viewer.viewportHeight = viewer.$refs.container.clientHeight;
      } else if (retry < 50) {
        setTimeout(() => this.fixPdfViewerViewport(retry + 1), 100);
      }
    },
    makeFileUrl(fileId, fileNo) {
      return (
        "/api/file/download/file?" +
        "file_id=" +
        fileId +
        "&file_no=" +
        fileNo +
        "&v=" +
        Date.now()
      );
    },
    handleDownload(data) {
      const link = document.createElement("a");
      link.href = data.src;
      link.download = "downloaded-file.pdf"; // 다운로드 파일 이름 설정
      link.click();
    },
    async pdfZip() {
      if (this.busyMessage) return;
      this.busyMessage = "PDF 압축 파일을 만드는 중입니다…";
      try {
        await CabinetApi.zip(this.activeDate, this.activeCategory);
      } catch (e) {
        this.notifyError(
          "압축 파일을 만들지 못했습니다. 잠시 후 다시 시도해 주세요."
        );
      } finally {
        this.busyMessage = "";
      }
    },
  },
};
</script>

<style scoped>
.loading-bar {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh; /* 페이지의 전체 높이를 사용하여 화면 중앙에 표시 */
  background-color: rgba(
    240,
    248,
    255,
    0.9
  ); /* 밝은 푸른색 계통의 배경으로 변경 */
  color: #2e4a62; /* 어두운 파란색 계통으로 텍스트 색상 변경 */
}

.loading-bar p {
  font-size: 20px;
  color: #333;
}

/* PDF 열기·zip 생성 대기 오버레이 — 레이어 팝업(z-index 낮음)보다 위에 고정 */
.busy-overlay {
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.35);
}

.busy-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  padding: 24px 32px;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.25);
}

.busy-box p {
  margin: 0;
  font-size: 15px;
  color: #2e4a62;
}

.busy-spinner {
  width: 36px;
  height: 36px;
  border: 4px solid #d6e2ee;
  border-top-color: #2e7dd6;
  border-radius: 50%;
  animation: busy-spin 0.8s linear infinite;
}

@keyframes busy-spin {
  to {
    transform: rotate(360deg);
  }
}

/* ───────── 우측 패널 재디자인 ─────────
   전역 style.css 의 .web-box/.header/.box 는 그대로 두고 cab-* 로 덮어쓴다. */
.cab-panel {
  --cab-bg: #0f1b2b;
  --cab-panel: #16263a;
  --cab-card: #1d3048;
  --cab-card-hover: #24405e;
  --cab-line: rgba(255, 255, 255, 0.08);
  --cab-text: #e8eef6;
  --cab-muted: #9fb0c4;
  --cab-accent: #3fa9f5;
  --cab-accent-2: #2b7fd1;
  width: 460px;
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: var(--cab-bg);
  color: var(--cab-text);
  font-family: "Noto Sans KR", "Open Sans", sans-serif;
  border-left: 1px solid var(--cab-line);
  box-sizing: border-box;
}

.cab-header {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 18px;
  background: var(--cab-panel);
  border-bottom: 1px solid var(--cab-line);
}

.cab-brand {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.cab-logo {
  width: 28px;
  height: 28px;
  flex-shrink: 0;
}

.cab-site {
  font-size: 14px;
  font-weight: 600;
  line-height: 1.35;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.cab-back {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px 6px 8px;
  border: 1px solid var(--cab-line);
  border-radius: 999px;
  background: transparent;
  color: var(--cab-text);
  font-size: 13px;
  cursor: pointer;
  transition: background 0.15s;
}

.cab-back:hover:not(:disabled) {
  background: var(--cab-card-hover);
}

.cab-back:disabled {
  opacity: 0.35;
  cursor: default;
}

.cab-back-arrow {
  font-size: 18px;
  line-height: 1;
}

.cab-crumbs {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  padding: 10px 18px 0;
  font-size: 12px;
  color: var(--cab-muted);
}

.cab-crumb.current {
  color: var(--cab-text);
  font-weight: 600;
}

.cab-crumb-sep {
  opacity: 0.5;
}

.cab-body {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: 12px 18px 18px;
}

.cab-section-head {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 12px;
}

.cab-section-title {
  margin: 0;
  font-size: 17px;
  font-weight: 700;
  letter-spacing: -0.01em;
}

.cab-section-meta {
  display: flex;
  align-items: center;
  gap: 10px;
}

.cab-count {
  font-size: 12px;
  color: var(--cab-muted);
}

.cab-action {
  padding: 7px 12px;
  border: none;
  border-radius: 6px;
  background: var(--cab-accent-2);
  color: #fff;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
}

.cab-action:hover:not(:disabled) {
  background: var(--cab-accent);
}

.cab-action:disabled {
  opacity: 0.4;
  cursor: default;
}

.cab-scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding-right: 2px;
}

.cab-scroll::-webkit-scrollbar {
  width: 8px;
}

.cab-scroll::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.15);
  border-radius: 8px;
}

.cab-empty {
  margin: 40px 0;
  text-align: center;
  color: var(--cab-muted);
  font-size: 13px;
}

/* 방 선택 카드 */
.cab-room {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px;
  margin-bottom: 10px;
  border: 1px solid var(--cab-line);
  border-radius: 12px;
  background: var(--cab-card);
  color: var(--cab-text);
  text-align: left;
  cursor: pointer;
  transition: background 0.15s, transform 0.1s;
}

.cab-room:hover {
  background: var(--cab-card-hover);
  transform: translateY(-1px);
}

.cab-room-icon {
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  border-radius: 10px;
  background-color: rgba(63, 169, 245, 0.12);
  background-repeat: no-repeat;
  background-position: center;
  background-size: 26px;
}

.cab-room-text {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
  flex: 1;
}

.cab-room-text strong {
  font-size: 15px;
}

.cab-room-text small {
  font-size: 12px;
  color: var(--cab-muted);
}

.cab-room-go,
.cab-row-go {
  font-size: 22px;
  color: var(--cab-muted);
  line-height: 1;
}

/* 타일 그리드(월·구분) */
.cab-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}

.cab-tile {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px 8px 14px;
  border: 1px solid var(--cab-line);
  border-radius: 12px;
  background: var(--cab-card);
  color: var(--cab-text);
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, transform 0.1s;
}

.cab-tile:hover {
  background: var(--cab-card-hover);
  transform: translateY(-1px);
}

.cab-tile.active {
  border-color: var(--cab-accent);
  box-shadow: 0 0 0 1px var(--cab-accent) inset;
}

.cab-tile-icon {
  width: 36px;
  height: 36px;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
}

.cab-tile-label {
  font-size: 13px;
  font-weight: 600;
  text-align: center;
  word-break: keep-all;
}

/* 성적서 목록 */
.cab-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.cab-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border: 1px solid var(--cab-line);
  border-radius: 10px;
  background: var(--cab-card);
  cursor: pointer;
  transition: background 0.15s;
}

.cab-row:hover {
  background: var(--cab-card-hover);
}

.cab-row-icon {
  width: 22px;
  height: 22px;
  flex-shrink: 0;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
}

.cab-row-main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.cab-row-no {
  font-size: 13.5px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cab-row-sub {
  font-size: 12px;
  color: var(--cab-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cab-badge {
  flex-shrink: 0;
  padding: 2px 8px;
  border-radius: 999px;
  background: rgba(63, 169, 245, 0.18);
  color: #9fd3ff;
  font-size: 11.5px;
  font-weight: 600;
}

/* 아이콘 배경 이미지는 public/css/style.css(.cab-ic-*)에 둔다 —
   scoped 스타일의 url() 은 css-loader 가 모듈로 해석해 빌드가 깨진다. */
</style>
